import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import {
  Button,
  Card,
  Field,
  Modal,
  PageHeader,
  Spinner,
  ErrorState,
  TextField,
} from "../../components/ui";
import { useOrg } from "../../lib/org";
import type { AdminUser, MemberRole } from "@shared/types";

interface UsersResponse {
  users: AdminUser[];
}

export default function AdminUsers() {
  const qc = useQueryClient();
  const { user: me } = useOrg();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => api.get<UsersResponse>("/admin/users"),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "users"] });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/admin/users/${id}`),
    onSuccess: invalidate,
  });

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Manage every account across the platform."
        action={<Button onClick={() => setCreating(true)}>Add user</Button>}
      />

      {isLoading && <Spinner label="Loading users…" />}
      {error && <ErrorState error={error} />}

      {data && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Orgs</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {u.email}
                    {u.id === me?.id && (
                      <span className="ml-2 text-xs text-gray-400">(you)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{u.name ?? "—"}</td>
                  <td className="px-4 py-3">
                    {u.isSuperAdmin ? (
                      <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-700">
                        Super admin
                      </span>
                    ) : (
                      <span className="text-gray-500">Member</span>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600">
                    {u.isSuperAdmin ? "All" : u.orgCount}
                  </td>
                  <td className="px-4 py-3">
                    {u.isActive ? (
                      <span className="text-green-700">Active</span>
                    ) : (
                      <span className="text-red-600">Disabled</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setAccessUser(u)}>
                        Book access
                      </Button>
                      <Button variant="secondary" onClick={() => setEditing(u)}>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        disabled={u.id === me?.id || del.isPending}
                        onClick={() => {
                          if (confirm(`Delete ${u.email}? This cannot be undone.`))
                            del.mutate(u.id);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidate();
          }}
        />
      )}
      {accessUser && (
        <BookAccessModal user={accessUser} onClose={() => setAccessUser(null)} />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          isSelf={editing.id === me?.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

interface BookAccessResponse {
  allOrganizations: boolean;
  orgs: { id: string; name: string; isActive: boolean; role: MemberRole | null }[];
}

function BookAccessModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "book-access", user.id],
    queryFn: () => api.get<BookAccessResponse>(`/admin/users/${user.id}/orgs`),
  });
  const save = useMutation({
    mutationFn: ({ orgId, role }: { orgId: string; role: string }) =>
      api.patch(`/admin/users/${user.id}/orgs/${orgId}`, { role: role || null }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "book-access", user.id] }),
        qc.invalidateQueries({ queryKey: ["admin", "users"] }),
        qc.invalidateQueries({ queryKey: ["session"] }),
      ]);
    },
  });
  return (
    <Modal open onClose={onClose} title={`Book access: ${user.email}`}>
      <div className="space-y-4">
        {isLoading && <Spinner label="Loading organizations…" />}
        {error && <ErrorState error={error} />}
        {data?.allOrganizations && (
          <p className="text-sm text-gray-600">Super admins automatically have admin access to all active organizations and their books, including new organizations.</p>
        )}
        {data?.orgs.map((org) => (
          <Field key={org.id} label={`${org.name}${org.isActive ? "" : " (inactive)"}`}>
            <select
              aria-label={`Access to ${org.name}`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={data.allOrganizations && org.isActive ? "admin" : org.role ?? ""}
              disabled={data.allOrganizations || save.isPending || !org.isActive}
              onChange={(e) => save.mutate({ orgId: org.id, role: e.target.value })}
            >
              <option value="">No access</option>
              <option value="viewer">Viewer — read only</option>
              <option value="accountant">Accountant — manage books</option>
              <option value="admin">Admin — books and organization settings</option>
              <option value="owner">Owner</option>
            </select>
          </Field>
        ))}
        {save.isPending && <p role="status" className="text-sm text-gray-500">Saving access…</p>}
        {save.isSuccess && <p role="status" className="text-sm text-green-700">Access saved.</p>}
        {save.error && <ErrorState error={save.error} />}
        <div className="flex justify-end"><Button variant="secondary" onClick={onClose}>Close</Button></div>
      </div>
    </Modal>
  );
}

function CreateUserModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    isSuperAdmin: false,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post("/admin/users", {
        email: form.email,
        name: form.name || undefined,
        password: form.password,
        isSuperAdmin: form.isSuperAdmin,
      }),
    onSuccess: onSaved,
  });

  return (
    <Modal open onClose={onClose} title="Add user">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Email">
          <TextField
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Name (optional)">
          <TextField
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Password" hint="At least 8 characters.">
          <TextField
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.isSuperAdmin}
            onChange={(e) =>
              setForm({ ...form, isSuperAdmin: e.target.checked })
            }
          />
          Grant super-admin access
        </label>

        {create.error && <ErrorState error={create.error} />}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create user"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({
  user,
  isSelf,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  isSelf: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(user.name ?? "");
  const [password, setPassword] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(user.isSuperAdmin);
  const [isActive, setIsActive] = useState(user.isActive);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      if (name !== (user.name ?? "")) patch.name = name || null;
      if (password) patch.password = password;
      if (isSuperAdmin !== user.isSuperAdmin) patch.isSuperAdmin = isSuperAdmin;
      if (isActive !== user.isActive) patch.isActive = isActive;
      return api.patch(`/admin/users/${user.id}`, patch);
    },
    onSuccess: onSaved,
  });

  return (
    <Modal open onClose={onClose} title={`Edit ${user.email}`}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name">
          <TextField value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Reset password" hint="Leave blank to keep current password.">
          <TextField
            type="password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password"
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isSuperAdmin}
            disabled={isSelf}
            onChange={(e) => setIsSuperAdmin(e.target.checked)}
          />
          Super-admin access
          {isSelf && (
            <span className="text-xs text-gray-400">
              (can&apos;t change your own)
            </span>
          )}
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isActive}
            disabled={isSelf}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Active
        </label>

        {save.error && <ErrorState error={save.error} />}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
